import { TooltipCell } from '@/features/approving_invoice_reports/components/List/Table/Columns/Cells/components/TooltipCell/TooltipCell';
import { InvoiceKindLabelMapping } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/Table/Constants/InvoiceKindLabelMapping';
import { InvoiceTransactionColumn } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import { TRANSACTION_MASTER_FIELD_NAMES } from '@/features/transaction_items/constants';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { toFloat } from '@/utils/convert/toNumber';
import type {
  InvoiceTransactionDetail,
  InvoiceTransactionDetailCrItem,
  InvoiceTransactionDetailCrSubItem,
  InvoiceTransactionDetailDept,
  InvoiceTransactionDetailDrExcise,
  InvoiceTransactionDetailExItem,
} from 'ap-openapi';
import classnames from 'classnames/bind';
import { format } from 'date-util';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

// `masterFieldName` is not a column property, but required for merging
// these default columns with the dynamic columns.
export type InvoiceTransactionDefaultColumn = InvoiceTransactionColumn & {
  masterFieldName: string;
};

export const useColumns = (): InvoiceTransactionDefaultColumn[] => {
  const { t } = useTranslation();

  return [
    {
      title: t('invoice_transaction_number'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.NUMBER,
      dataIndex: 'number',
      render(value) {
        return value;
      },
      width: '80px',
      fixed: 'left',
    },
    {
      title: t('invoice_transaction_deal_date'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.DEAL_DATE,
      dataIndex: 'deal_date',
      key: 'deal_date',
      render(value: string) {
        return format(value, 'YYYY/MM/DD');
      },
      width: '104px',
    },
    {
      title: t('invoice_transaction_name'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.NAME,
      dataIndex: 'name',
      key: 'name',
      width: '200px',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
    },
    {
      title: t('invoice_transaction_department'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.DEPT,
      dataIndex: 'dept',
      width: '128px',
      render(value: InvoiceTransactionDetailDept) {
        return (
          <TooltipCell title={value?.name}>{value?.name || ''}</TooltipCell>
        );
      },
    },
    {
      title: t('invoice_transaction_expense_account'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.EX_ITEM,
      dataIndex: 'ex_item',
      render(value: InvoiceTransactionDetailExItem) {
        return (
          <TooltipCell title={value?.name}>{value?.name || ''}</TooltipCell>
        );
      },
      width: '128px',
    },
    {
      title: t('invoice_transaction_unit'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.UNIT,
      dataIndex: 'unit_value',
      key: 'unit_value',
      align: 'right',
      render(value) {
        return toCommaSeparated(value);
      },
      width: '120px',
    },
    {
      title: t('invoice_transaction_quantity'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.QUANTITY,
      dataIndex: 'quantity',
      key: 'quantity',
      render(value) {
        return toCommaSeparated(value);
      },
      align: 'right',
      width: '80px',
    },
    {
      title: t('invoice_transaction_total_value'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.AMOUNT,
      dataIndex: 'total_value',
      key: 'total_value',
      align: 'right',
      render(value, record) {
        return record.currency === 'JPY' ? (
          toCommaSeparated(value)
        ) : (
          <TooltipCell
            title={t(
              'approving_invoice_reports.transactions.total_value_tax_include_tooltip'
            )}
            className={cx(styles['drawerTooltipText'])}
          >
            {toCommaSeparated(value)}
          </TooltipCell>
        );
      },
      width: '128px',
    },
    {
      title: t('invoice_transaction_dr_excise_id'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.TAX_CATEGORY,
      dataIndex: 'dr_excise',
      render(value: InvoiceTransactionDetailDrExcise) {
        return value?.name;
      },
      width: '152px',
    },
    {
      title: t('invoice_transaction_invoice_kind'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.INVOICE_KIND,
      dataIndex: 'invoice_kind',
      render(value: keyof typeof InvoiceKindLabelMapping) {
        return value ? InvoiceKindLabelMapping[value] : '';
      },
      width: '152px',
    },
    {
      title: t('invoice_transaction_excise_value'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.TAX_AMOUNT,
      dataIndex: 'excise_value',
      render(value) {
        return toCommaSeparated(value);
      },
      width: '96px',
      align: 'right',
    },
    {
      title: t('invoice_transaction_has_withholding_income_tax'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.WITHHOLDING_TAX_CHECK,
      dataIndex: 'has_withholding_income_tax',
      render(value) {
        return value
          ? t('approving_invoice_reports.table.columns.yes')
          : t('approving_invoice_reports.table.columns.no');
      },
      width: '72px',
    },
    {
      title: t('approving_invoice_report_detail_withholding_tax'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.WITHHOLDING_TAX_AMOUNT,
      dataIndex: 'withholding_income_tax_value',
      render(value) {
        return toCommaSeparated(value);
      },
      width: '96px',
      align: 'right',
    },

    {
      title: t('invoice_transaction_memo'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.MEMO,
      dataIndex: 'memo',
      render(value) {
        return <TooltipCell title={value}>{value || ''}</TooltipCell>;
      },
      width: '220px',
    },
    {
      title: t('invoice_transaction_project'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.PROJECT,
      dataIndex: 'project_code',
      render(_, row: InvoiceTransactionDetail) {
        const name = row.project?.name ?? row.project_code?.name;
        return <TooltipCell title={name}>{name || ''}</TooltipCell>;
      },
      width: '160px',
    },
    {
      title: t('invoice_transaction_credit_account'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.CR_ITEM,
      dataIndex: 'cr_item',
      render(value: InvoiceTransactionDetailCrItem) {
        return (
          <TooltipCell title={value?.name}>{value?.name || ''}</TooltipCell>
        );
      },
      width: '128px',
    },
    {
      title: t('invoice_transaction_credit_sub_account'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.CR_SUB_ITEM,
      dataIndex: 'cr_sub_item',
      render(value: InvoiceTransactionDetailCrSubItem) {
        return (
          <TooltipCell title={value?.name}>{value?.name || ''}</TooltipCell>
        );
      },
      width: '128px',
    },

    {
      title: t('invoice_transaction_currency'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.CURRENCY,
      dataIndex: 'currency',
      render(value) {
        return value;
      },
      width: '80px',
    },
    {
      title: t('invoice_transaction_jpyRate'),
      masterFieldName: TRANSACTION_MASTER_FIELD_NAMES.JPY_RATE,
      dataIndex: 'jpyrate',
      render(value) {
        const floatValue = toFloat(value);
        const rounded = parseFloat(floatValue.toFixed(1));
        return floatValue === rounded ? floatValue.toFixed(1) : floatValue;
      },
      width: '128px',
      align: 'right',
    },
  ];
};
